import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../../lib/api";
import type { ProvisionedStation } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { SettingRow } from "../ui/SettingRow";
import { StationCredential } from "./StationCredential";

/**
 * Rotating the station's password.
 *
 * Two clicks, because one click is not enough for an action that invalidates the
 * credential a station in the field is using right now. The station keeps working
 * on its existing socket — the password is checked at the WebSocket upgrade, not
 * per frame — but it will fail to reconnect until someone configures the new one,
 * which is exactly what the confirmation says.
 *
 * There is no standing explanation of hashing: "the password is an argon2id hash
 * and cannot be shown again" was permanent furniture on a page nobody opens to read
 * about hashing, and the confirmation says the part that matters — it is shown
 * once — at the moment it is true.
 */
export function RotatePassword({
  chargePointId,
  connected,
}: {
  readonly chargePointId: string;
  readonly connected: boolean;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [rotated, setRotated] = useState<ProvisionedStation | undefined>(undefined);

  const rotate = useMutation({
    mutationFn: () =>
      post<ProvisionedStation>(
        `/charge-points/${encodeURIComponent(chargePointId)}/rotate-password`,
      ),
    onSuccess: (station) => {
      setIsConfirming(false);
      setRotated(station);
    },
  });

  const isIdle = !isConfirming && rotated === undefined;

  return (
    <SettingRow
      label="Station password"
      value="Set at registration; it cannot be shown again"
      hint="Rotate it when the old one may have leaked or the station is handed to someone new."
      actions={
        isIdle ? (
          <Button variant="danger" onClick={() => setIsConfirming(true)}>
            Rotate password
          </Button>
        ) : null
      }
    >
      {rotated === undefined ? null : (
        <StationCredential
          station={rotated}
          heading={`New password for ${rotated.chargePoint.id}`}
          onDismiss={() => setRotated(undefined)}
        />
      )}

      {rotate.isError ? <ErrorNotice error={rotate.error} /> : null}

      {isConfirming ? (
        <div className="flex flex-col gap-3 border-l-2 border-signal bg-paper-raised px-4 py-3">
          <p className="m-0 max-w-prose text-body text-ink">
            Rotating replaces this station's password immediately. The new one is shown once and
            cannot be recovered.
            {connected
              ? " The live connection survives until the station next reconnects, so there is time to configure it."
              : " This station is not connected, so it will stay offline until the new password is configured on it."}
          </p>
          <div className="flex items-center gap-3">
            <Button variant="danger" onClick={() => rotate.mutate()} pending={rotate.isPending}>
              Rotate it
            </Button>
            <Button variant="ghost" onClick={() => setIsConfirming(false)}>
              Keep the current password
            </Button>
          </div>
        </div>
      ) : null}
    </SettingRow>
  );
}
